/**
 * AppHeader - Header principal de la aplicación
 *
 * Layout: [logo] MyChat [← Volver] ··· [avatar]
 * No sidebar toggle here — sidebar opens via hover edge trigger or Cmd+\.
 * Close button lives inside UnifiedSidebar header (PanelLeftClose).
 */

import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import UserMenu from './UserMenu';

interface AppHeaderProps {
  currentUser: any;
  onLogout?: () => void;
  onOpenProfile?: () => void;
  onOpenSettings?: () => void;
  isChatActive?: boolean;
  onClose?: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  currentUser,
  onLogout,
  onOpenProfile,
  onOpenSettings,
  isChatActive = false,
  onClose,
}) => {
  const { theme } = useTheme();

  return (
    <header
      className={`
      flex items-center gap-3 px-4 py-3
      border-b ${theme.colors.border.primary}
      ${theme.colors.bg.primary}
    `}
    >
      {/* Brand mark + wordmark */}
      <div className="flex items-center gap-2">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          className="text-nb-accent flex-shrink-0"
          aria-hidden="true"
        >
          {/* Chat bubble */}
          <path
            d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2z"
            fill="currentColor"
            opacity="0.15"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          {/* Document page */}
          <rect x="9" y="7" width="6" height="8" rx="1" fill="currentColor" opacity="0.6" />
          <line
            x1="10.5"
            y1="9.5"
            x2="13.5"
            y2="9.5"
            stroke="var(--nb-bg)"
            strokeWidth="1"
            strokeLinecap="round"
          />
          <line
            x1="10.5"
            y1="11.5"
            x2="13.5"
            y2="11.5"
            stroke="var(--nb-bg)"
            strokeWidth="1"
            strokeLinecap="round"
          />
          <line
            x1="10.5"
            y1="13.5"
            x2="12.5"
            y2="13.5"
            stroke="var(--nb-bg)"
            strokeWidth="1"
            strokeLinecap="round"
          />
        </svg>
        <span className={`text-xl font-bold ${theme.colors.text.primary}`}>MyChat</span>
      </div>

      {/* Close/back button for non-chat views */}
      {!isChatActive && onClose && (
        <button
          onClick={onClose}
          className={`flex items-center gap-1 px-2 py-1 rounded-lg ${theme.colors.text.secondary} hover:${theme.colors.state.hover} transition-colors text-sm`}
          aria-label="Volver al chat"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Volver</span>
        </button>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        {/* User Menu */}
        {currentUser && onLogout && (
          <UserMenu
            currentUser={currentUser}
            onLogout={onLogout}
            onOpenProfile={onOpenProfile}
            onOpenSettings={onOpenSettings}
          />
        )}
      </div>
    </header>
  );
};

export default AppHeader;
